import { Permission } from "@sarva/domain-types";
import type { IconName } from "@sarva/ui";

/**
 * The sidebar's structure, declared as data (doc 40 §8).
 *
 * Flat items and one level of grouping — the reference system's shape, and the
 * ceiling on nesting is deliberate: a third level in a left rail is a menu
 * pretending to be navigation.
 *
 * (H) doc 27 §4: every permission here is a value from the generated
 * `Permission` enum. A string literal for a permission is a blocking review
 * finding, because a typo'd literal fails open — the gate passes and the API
 * refuses, which is the worst of both.
 */

export interface NavLeaf {
  kind: "leaf";
  to: string;
  label: string;
  icon: IconName;
  /**
   * The API's own gate on this screen's load-bearing read.
   *
   * **`undefined` means the destination is not gated at all, and exactly one leaf may say
   * so.** That is not a convenience: it is the landing route. Every role signs in to `/`,
   * the Dashboard composes seven panels that each gate themselves, and gating the *link*
   * on one of their permissions made the rail contradict the router — a front desk holds
   * no `report:read`, so the product rendered "Dashboard — this action requires the
   * report:read permission" as a disabled row describing the page they were standing on.
   *
   * An ungated leaf fails open by construction, which is the shape doc 27 §4 exists to
   * forbid, so `nav.built.test.ts` holds the door: it asserts that at most one leaf omits
   * a permission and that it is `/`. A second one is a failing build, not a review note.
   */
  permission?: Permission;
  /**
   * Navigation is the one place `hide` is usually right: a disabled nav link
   * to a page a user can never open is noise, not an explanation. Anything a
   * user might reasonably expect access to stays visible and disabled instead
   * (doc 27 §7).
   */
  whenDenied: "hide" | "disable";
  /** Shown as a count chip. In the real app this comes from a Query hook. */
  badgeKey?: "queue" | "obligations";
  /**
   * Whether the screen behind this link exists yet. Absent means it does.
   *
   * A nav link to an unregistered route renders "That page does not exist",
   * which is strictly worse than not offering the link: it reads as a broken
   * product rather than an unbuilt one. Permission gating used to hide most of
   * these by accident — the roles that could see them did not exist in any dev
   * tenant — so widening a dev user's grants exposed nine dead links at once.
   *
   * **The remedy is disabled, not hidden.** Filtering these out of the rail
   * traded one wrong impression for another: a product that looks like it has
   * four screens rather than one that is visibly part-built. So the sidebar
   * renders an unbuilt leaf as a **non-navigable, `aria-disabled` row** — same
   * label and icon, no `<a href>`, and a reason a keyboard user can reach. It
   * is the same ruling this interface already makes for `whenDenied`: anything
   * a user might reasonably expect stays visible and disabled.
   *
   * `NAV_BUILT` still exists, for the ⌘K palette — a destination you cannot
   * open is a search result that wastes a keystroke.
   *
   * `nav.built.test.ts` asserts this flag against the router's registered
   * paths in both directions, so a screen that lands without its flag being
   * cleared, or a flag cleared before the screen lands, fails the build.
   */
  built?: false;
}

export interface NavGroup {
  kind: "group";
  label: string;
  icon: IconName;
  children: readonly NavLeaf[];
  /** Groups start closed unless they contain the landing route. */
  defaultOpen?: boolean;
}

export type NavNode = NavLeaf | NavGroup;

/**
 * `permission` is `Permission | null` rather than optional: an ungated destination has to
 * be **written down** as one. Making the parameter optional would let a leaf lose its gate
 * by omission, which is the same failure doc 27 §4 refuses for string literals — it passes
 * the gate and the API refuses, the worst of both.
 */
const leaf = (
  to: string,
  label: string,
  icon: IconName,
  permission: Permission | null,
  extra: Partial<Pick<NavLeaf, "whenDenied" | "badgeKey" | "built">> = {},
): NavLeaf => ({
  kind: "leaf",
  to,
  label,
  icon,
  ...(permission === null ? {} : { permission }),
  whenDenied: extra.whenDenied ?? "disable",
  ...(extra.badgeKey === undefined ? {} : { badgeKey: extra.badgeKey }),
  ...(extra.built === undefined ? {} : { built: extra.built }),
});

export const NAV: readonly NavNode[] = [
  // (H) Ungated, and the only leaf that is. This read `Permission.REPORT_READ`, which is
  // the gate on two of its seven panels and on none of the other five — so the rail told
  // every role without `report:read` that it required a permission to open the page it had
  // just been sent to. Measured as a front desk: six of seven panels render their designed
  // no-permission state, the seventh (Follow-ups due) works, and the rail showed
  // "Dashboard" as a disabled row. Each panel gates itself on its own endpoint's
  // permission, which is where the gate belongs; the link to the landing route is not a
  // claim about what anybody may read.
  //
  // What this does NOT fix: a front desk still lands on a screen that is mostly refusals.
  // That is a product question about where a role should land, not a permission bug, and
  // it is recorded rather than guessed at.
  leaf("/", "Dashboard", "dashboard", null),
  leaf("/queue", "Work queue", "queue", Permission.WORK_ITEM_READ, {
    badgeKey: "queue",
  }),
  // Top level rather than inside a group, so it costs a row unconditionally (the
  // rail shows one group at a time, so a group's children are free while it is
  // closed — D27). It earns that: a front desk
  // opens the schedule every few minutes, and `appointment:read` is the API's
  // own gate on `GET /appointments`.
  leaf("/schedule", "Schedule", "calendar", Permission.APPOINTMENT_READ),
  // Fourth and fifth, above every group (D31). A front desk opens a patient and a
  // balance all day; these sat ninth and tenth, below three groups, because of the
  // order the screens were built in rather than how often they are opened.
  // (H) doc 27 §7: `patient:read`, and this entry has just changed.
  //
  // It read `claim:read` for as long as the Patients screen was claim activity
  // derived from `GET /claims` — there was no patient resource in the API, so
  // gating on the permission the screen was *named* after would have passed for
  // sessions the API refuses. `GET /patients` exists now and declares
  // `patient:read`, so the gate moves with the request rather than staying
  // where a comment left it.
  leaf("/patients", "Patients", "users", Permission.PATIENT_READ),
  // D180, D231 (H34): assistants' notes waiting for the supervising clinician — a daily
  // clinical task, so beside Patients rather than among the reference data.
  leaf("/cosign-queue", "Notes to co-sign", "check-circle", Permission.NOTE_COSIGN),
  // D296: plans of care whose physician certification is unsigned — chased daily by billing.
  leaf("/plan-certifications", "Plan certifications", "check-circle", Permission.CARE_PLAN_READ),
  // Same permission, and now for the same ordinary reason: this screen's
  // endpoints declare `patient:read` too.
  leaf(
    "/patient-balance",
    "Patient balance",
    "wallet",
    Permission.PATIENT_READ,
  ),
  {
    kind: "group",
    label: "Revenue cycle",
    icon: "claim",
    defaultOpen: true,
    children: [
      // `/claims`, the AR worklist — **not** a claim number.
      //
      // This entry pointed at `/claims/CLM-2026-004417` for as long as it
      // existed: a claim number hardcoded from whichever tenant it was copied
      // out of, so in every other tenant the first item under Revenue cycle
      // opened "Not found — No claim in this practice has the number
      // CLM-2026-004417". `GET /claims` was live the whole time and consumed by
      // nothing as a collection.
      //
      // `nav.built.test.ts` could not see it: the link *resolved*, because
      // `claims/:claimNumber` is a registered route. A parameterised target is
      // how "the route exists" and "the screen answers" come apart, and no nav
      // entry needs a literal row id any more.
      leaf("/claims", "Claims", "claim", Permission.CLAIM_READ),
      // The start of the cycle, and the only member of this group that is not
      // about a claim yet. `GET /encounters` — the uncoded worklist this screen
      // opens on — declares `charge:read`, the same grant as the per-encounter
      // read; entering, releasing and recognising declare three other grants and
      // are gated at their own controls inside the screen.
      leaf("/charges", "Charge capture", "receipt", Permission.CHARGE_READ),
      // M12, beside Charge capture because it is the step before it: the
      // worklist is encounters with nothing billed, which is the same question
      // Charge capture opens on. `GET /encounters` declares `charge:read`, so
      // that is the gate; presenting a note declares `document:write` and the
      // two model calls declare `coding_suggestion:propose`, each gated at its
      // own control. `coder` holds all of them and nothing else holds
      // `propose` or `accept` — a reviewer sees the review and no controls,
      // which is ADR 0002's separation rather than a gap.
      leaf("/coding", "Coding", "code", Permission.CHARGE_READ),
      // The API's own route declarations, from
      // `services/api/src/sarva_api/routers/`: `denial:read` on
      // `GET /denials`, `appeal:read` on `GET /appeals`, `remittance:read` on
      // `GET /remittances`, and `payment:post` on `POST /remittances/{id}:post`.
      leaf("/denials", "Denials", "octagon", Permission.DENIAL_READ),
      leaf("/appeals", "Appeals", "scale", Permission.APPEAL_READ),
      leaf("/remittance", "Remittance", "inbox", Permission.REMITTANCE_READ),
      // Posting gates on the permission for the action, not for the read. The
      // screen also lists remittances (`remittance:read`) and gates that table
      // separately — a person who can post but cannot read the list gets the
      // designed no-permission state on the table rather than an empty page.
      leaf("/posting", "Posting", "check-circle", Permission.PAYMENT_POST),
      // GAP-059, immediately after Posting because it is the same question
      // answered in the other direction: Posting is money arriving, this is
      // money the practice holds and is not owed. `GET /refunds` declares
      // `payment:read` — explicitly **not** `refund:initiate`, which the route
      // notes "somebody who may not request one still has to be able to see
      // that the practice owes a patient £60". Requesting, approving and
      // issuing are gated at their own controls inside the screen, and
      // `refund:initiate` is held by no role yet ([D125]), so the request panel
      // renders disabled with the reason on it.
      leaf("/refunds", "Refunds", "undo", Permission.PAYMENT_READ),
      // M17. `GET /adjustments` declares `ledger:read` — it declared
      // `adjustment:apply` until this screen was built, which made the pending
      // queue unreadable by `practice_owner`, the only role that can clear it.
      // Asking declares `adjustment:apply` and agreeing declares
      // `write_off:approve`; both are gated at their own controls, and **no
      // role holds both**, so a session sees one or the other and never a
      // single button that writes money off alone.
      leaf("/write-offs", "Write-offs", "file-minus", Permission.LEDGER_READ),
      // GAP-062, the end of the cycle. `GET /accounting-periods` declares
      // `ledger:read`, so that is the gate — not `period:close`, which is
      // `practice_owner`'s alone. A revenue-cycle manager needs to see where
      // the month stands without being the person who locks it, and gating the
      // link on the write grant would hide the figures from whoever prepares
      // them.
      leaf("/month-end", "Month-end close", "lock", Permission.LEDGER_READ),
      // GAP-041, beside Month-end close because it is the other half of the same
      // question: the close asks whether the month ties internally, and this asks
      // whether it ties to the bank. `GET /bank-deposits` declares
      // `remittance:read`, so that is the gate — loading a statement declares
      // `remittance:ingest` and is gated at its own control.
      leaf(
        "/bank-reconciliation",
        "Bank reconciliation",
        "landmark",
        Permission.REMITTANCE_READ,
      ),
    ],
  },
  {
    kind: "group",
    label: "Deadlines",
    icon: "clock",
    children: [
      leaf("/obligations", "Obligations", "clock", Permission.OBLIGATION_READ, {
        badgeKey: "obligations",
      }),
      // (H) Listed, because it was built and read as missing. The panel has been on the
      // dashboard since M13; the SME looked for a worklist, found none, and recorded the
      // capability as absent. A destination nobody can name is a destination nobody finds.
      leaf(
        "/follow-ups",
        "Follow-ups due",
        "message",
        Permission.FOLLOW_UP_NOTE_READ,
      ),

      // Under Deadlines rather than under Revenue cycle, and the reason is
      // both domain and measurement. Domain: the only thing that makes an
      // unresolved filing attempt urgent is that the timely-filing limit keeps
      // running while nobody can send the claim again — it is a deadline
      // surface that happens to be about a claim. Measurement: Revenue cycle
      // is already the group that sets the rail's worst case (D27, D31).
      leaf(
        "/claim-submissions",
        "Claim submissions",
        "send",
        Permission.CLAIM_READ,
      ),
      // Under Deadlines for the reason Claim submissions is: a 999 or a 277CA
      // rejection means the claim was refused **before** adjudication, so it was
      // never filed — and the timely-filing limit keeps running while it sits
      // there looking answered. `claim:read` is the gate, because that is what
      // `GET /claims/{id}/acknowledgements` declares; repairing declares
      // `claim:resubmit` and is gated at the control.
      leaf(
        "/acknowledgements",
        "Acknowledgements",
        "mail-check",
        Permission.CLAIM_READ,
      ),
      // Backend D348: for a practice whose records live in its own system, what was done here
      // and must be recorded there. `GET /writeback-tasks` declares `writeback_task:read`.
      leaf(
        "/writeback",
        "Record in client system",
        "clipboard-check",
        Permission.WRITEBACK_TASK_READ,
      ),
      // An authorization is a clock: it expires, and what is left of it runs
      // out. `GET /patients/{id}/prior-authorizations` declares
      // `prior_auth:read`; the payer-requirement table inside the screen gates
      // separately on `payer:read`, because that is payer configuration rather
      // than a patient's record.
      leaf(
        "/prior-auth",
        "Prior authorization",
        "clipboard-check",
        Permission.PRIOR_AUTH_READ,
      ),
      // (H) Listed, because it was built and read as missing — GAP-019, the third of
      // these. The expiry worklist has been the second panel on Prior authorization since
      // it was built, above the fold, and the review still recorded "no expiring-referrals
      // list found". The reviewer was right about what they could see: **the word
      // *referral* appeared nowhere in this rail**, so the only referrals in the product
      // were the ones on a patient's chart.
      //
      // Directly below Prior authorization because it is the same kind of clock run by a
      // different party — the PCP issues a referral, the payer grants an authorization —
      // and a reader who wants one will often want the other. `GET /referrals` declares
      // `referral:read`; amending declares `referral:write` and is gated on the row.
      leaf("/referrals", "Referrals", "forward", Permission.REFERRAL_READ),
    ],
  },
  {
    kind: "group",
    label: "Automation",
    icon: "robot",
    children: [
      // (H) doc 27 §7: `work_item:read`, NOT `automation:read`.
      //
      // `automation:read` is in `contracts/permissions.yaml` and **no endpoint
      // declares it** — checkable in `openapi.json`, where every operation
      // states its own permission. There is no agent registry and no run log to
      // read. What the contract can answer about automated work is R-WM-07's
      // queue health, and `GET /work-queues/health` declares `work_item:read`.
      //
      // Gating on the permission the screen is named after would have been
      // wrong in both directions at once: a `practice_admin` holds
      // `automation:read` and not `work_item:read`, so the link would have been
      // offered to the one role whose request the API refuses, and a `biller`
      // holds `work_item:read` and not `automation:read`, so it would have been
      // hidden from the role that works the queues it describes. This moves to
      // `automation:read` on the day an agent endpoint declares it.
      leaf("/agents", "Agent activity", "robot", Permission.WORK_ITEM_READ),
      // `policy_band:read` is kept, and it is the one leaf here gating on a
      // permission no endpoint declares — for the same reason `/reports` does,
      // only more so. Reports composes four lists and has no single API
      // permission to mirror; this screen **makes no request at all**, because
      // nothing reads a policy band, so there is no endpoint permission that
      // could be more correct. It is the product's gate for the autonomy
      // surface, and it moves to a band endpoint's own permission on the day
      // one exists.
      leaf(
        "/policy-bands",
        "Policy bands",
        "gauge",
        Permission.POLICY_BAND_READ,
      ),
      // `GET /payer-rules` declares `payer:read`, so that is the gate — not
      // `policy_band:edit`, which is what *answering* a proposal needs. A
      // biller can read the queue and cannot promote from it, and gating the
      // link on the write permission would hide the queue from the people who
      // work it.
      leaf("/payer-rules", "Payer rules", "sparkles", Permission.PAYER_READ),
      // GAP-034. Gated on `scrub_rule:read`, which is what `GET /scrub-rules` declares —
      // not on the propose or approve grants, because reading which edits are in force is
      // what a biller needs to understand why a claim was stopped.
      leaf(
        "/scrub-rules",
        "Payer edits",
        "list-checks",
        Permission.SCRUB_RULE_READ,
      ),
      // Beside Payer rules because it is the same shape of surface: a model
      // proposes and a person answers. `GET /document-classifications` declares
      // `document:read`; classifying declares `document:write`, because the call
      // retains the document's bytes whatever it concludes.
      leaf("/documents", "Documents", "document", Permission.DOCUMENT_READ),
    ],
  },
  // ── Insights (D31) ────────────────────────────────────────────────────────
  //
  // The figures a practice owner reads weekly, grouped so they cost one row while closed.
  // Recovery came out of Revenue cycle to join them: it is a report (`report:read`, the
  // baseline diagnostic) with sweeps attached, not a step in the cycle. The daily glance
  // at the scoreboard happens on the Dashboard's cards, so one more click here is cheap.
  {
    kind: "group",
    label: "Insights",
    icon: "chart",
    children: [
      // `report:read`, and now for the API's own reason rather than the product's.
      //
      // This entry used to carry the permission as "the product's gate for the
      // reporting surface as a whole", because the screen composed four ranked
      // lists each gated on its own endpoint inside the screen and there was no
      // single API permission to mirror. The screen is an **index** now, and the
      // three cards on it read `GET /reports/ar-aging` and
      // `GET /reports/balance-drift`, both of which declare `report:read` — so the
      // gate on the link is the gate on what the link's own figures come from. The
      // four rankings are still gated on their endpoints' permissions inside the
      // page, which is where a panel's gate belongs (doc 27 §7).
      //
      // (H) One leaf, not four. `/reports/ar-aging`, `/reports/balance-drift` and
      // `/reports/exports` are reached from the index's cards: `nav.built.test.ts`
      // refuses a multi-segment nav target outright, and a rail entry per report
      // would put four rows where one belongs on a rail that already overflows at
      // 720px (D27). See `features/reports/components/ReportsIndexPage.tsx`.
      // D196: `GET /reports/practice-activity` declares `report:read`. First: it is the figure
      // an owner opens weekly.
      leaf("/activity", "Practice activity", "users", Permission.REPORT_READ),
      leaf("/reports", "Reports", "chart", Permission.REPORT_READ),
      // `GET /reports/scoreboard` really does declare `report:read` — unlike the
      // Reports screen above, this one gate is the API's own.
      leaf("/scoreboard", "Scoreboard", "trend-up", Permission.REPORT_READ),
      // `GET /reports/baseline-diagnostic` declares `report:read`; the three
      // sweeps declare `automation:run` plus a second grant each, and the
      // screen gates each button on its own pair. `report:read` is the gate for
      // opening it, because that is the read the screen makes on load.
      leaf("/recovery", "Recovery", "trend-up", Permission.REPORT_READ),
    ],
  },
  // ── How the rail is ordered, and what it costs (D27, D31) ─────────────────
  //
  // Order is by how often a destination is opened, not by how the product is modelled.
  // That is an estimate from each role's job, not telemetry; revisit it when there is usage
  // data. The rail is (top-level leaves) + (group headers) + (children of the **one** open
  // group): 5 + 5 = 10 rows closed, and 21 at worst with Revenue cycle (11) open. Overflow
  // is still possible at 720px, and what it truncates is a choice: Reference data is last
  // because it is consulted when onboarding, not daily.
  {
    kind: "group",
    label: "Reference data",
    icon: "columns",
    children: [
      // `GET /imports` declares `import:read`; staging, signing off and committing declare
      // their own permissions and are gated at the controls that perform them.
      leaf("/imports", "Data import", "upload", Permission.IMPORT_READ),
      // `GET /payers` declares `payer:read`; matching one to the directory declares
      // `payer:write` and is gated at the control, because those are different roles —
      // `practice_admin` configures, `biller` reads.
      leaf("/payers", "Payers", "building", Permission.PAYER_READ),
      leaf(
        "/fee-schedules",
        "Fee schedules",
        "tag",
        Permission.FEE_SCHEDULE_READ,
      ),
      // (H) Listed, because it was built and read as missing — GAP-029, and the verdict
      // ("Not found — no code-set screen") was literally true: the panel is the last thing
      // on a long Charge capture page, under a heading that says "Code masters" to somebody
      // looking for "code sets". It stays there, where a coder staring at a refused charge
      // already is; this is for the coder who just wants to know whether the 2026 book is
      // loaded, and Reference data is where they look.
      //
      // Beside Fee schedules because both answer "which book is in force", one for codes
      // and one for rates. `GET /code-sets` declares `code_set:read`. There is no write
      // gate to name: `code_set:write` is held by nobody and a master is loaded by a
      // migration, so the screen has no controls and is not missing any.
      leaf("/code-sets", "Code sets", "book", Permission.CODE_SET_READ),
      // GAP — the partner service map ([ADR 0016]). Beside Code sets because both answer
      // "what does this mean in billing terms": one for our own masters, one for a partner's
      // catalogue. `GET /service-map` declares `charge:read` and the write declares
      // `charge:write` — the same pair superbills use, and for the same reason: a mapping's
      // only purpose is to produce charge lines, so whoever may enter a charge may say what a
      // service maps to. A grant of its own would leave a practice waiting on an administrator
      // to map a service it sells daily.
      leaf("/service-map", "Service map", "network", Permission.CHARGE_READ),
      // Clinical setup (ADR 0017, D168-D171): note templates, specialty packs, charge rules,
      // codes and unit methods. `GET /note-templates` declares `note_template:read`, the
      // screen's first read; the billing tabs gate themselves on `charge_rule:read`.
      leaf("/clinical-setup", "Clinical setup", "clipboard-check", Permission.NOTE_TEMPLATE_READ),
      leaf("/providers", "Providers", "stethoscope", Permission.PROVIDER_READ),
      leaf("/locations", "Locations", "map-pin", Permission.LOCATION_READ),
      // M01's configuration, which the API had and no screen reached: nine
      // operations with no caller, so a practice could not say what a visit type is
      // or when a provider works. `GET /visit-types`, `GET /schedule-templates` and
      // `GET /schedule-blocks` all declare `schedule_template:read`, so that is the
      // gate; every write declares `schedule_template:write` and is gated at its
      // own control. Configuration, opened at onboarding and rarely after, so
      // it lives here rather than beside Schedule (D31).
      leaf(
        "/scheduling-setup",
        "Scheduling setup",
        "sliders",
        Permission.SCHEDULE_TEMPLATE_READ,
      ),
      // The API's practice routes declare `tenant_setting:read` to list and
      // `tenant_setting:edit` to change (services/api routers/practices.py), so
      // that is what the nav gates on. The UI's idea of "allowed" is the API's or
      // it is worthless (doc 27 §7).
      leaf(
        "/practices",
        "Practices",
        "building",
        Permission.TENANT_SETTING_READ,
      ),
      // Gated on `user:read` — seeing who has an account. Inviting and granting
      // declare `user:invite` and `role:assign`, and are gated at their controls.
      leaf("/administration", "Users and roles", "users", Permission.USER_READ),
      // `tenant_setting:read` sees the values. Changing each one declares that
      // setting's OWN permission, read from its definition and gated per row.
      leaf("/settings", "Settings", "cog", Permission.TENANT_SETTING_READ),
      // `audit:read` is the auditor's grant, and it is PHI-tiered: the before and
      // after snapshots carry whatever the record carried.
      leaf("/audit", "Audit log", "eye", Permission.AUDIT_READ),
      // The Email log (backend D173, D340): `GET /email-outbox` declares `tenant_setting:read`.
      leaf("/email-log", "Email log", "mail", Permission.TENANT_SETTING_READ),
    ],
  },
];

/** Flattened, for route matching and for the shortcut sheet. */
export const NAV_LEAVES: readonly NavLeaf[] = NAV.flatMap((node) =>
  node.kind === "leaf" ? [node] : node.children,
);

/**
 * `NAV` with the unbuilt screens removed, and any group left empty dropped
 * with them.
 *
 * This is what the **⌘K palette** renders. It is no longer what the sidebar
 * renders: the rail shows the whole product surface with the unbuilt
 * destinations disabled (see `built` above). A palette is different — its whole
 * job is "type a few letters, press Enter, arrive", and an entry that cannot be
 * arrived at is a result that wastes the keystroke.
 */
export const NAV_BUILT: readonly NavNode[] = NAV.flatMap<NavNode>((node) => {
  if (node.kind === "leaf") return node.built === false ? [] : [node];
  const children = node.children.filter((child) => child.built !== false);
  return children.length === 0 ? [] : [{ ...node, children }];
});

/** Flattened built leaves — route matching and the shortcut sheet want these. */
export const NAV_BUILT_LEAVES: readonly NavLeaf[] = NAV_BUILT.flatMap((node) =>
  node.kind === "leaf" ? [node] : node.children,
);
